import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Upload, X } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { generateServiceReport } from '@/services/ProtoServiceTemplate'
import { uploadDocument } from '@/services/document-upload.service'
import {
  STMIDBEquipmentService,
  getSTMIDBUploadCode,
  isSTMIDBCompany,
  mapWLLDocumentTypeToSTMIDB,
} from '@/services/api/stmidbEquipmentService'
import {
  SERVICE_DEFAULT_SITE,
  SERVICE_REPORT_SITES,
  SERVICE_SPARE_PARTS_OPTIONS,
  SERVICE_WORK_PERFORMED_OPTIONS,
  emptyServiceEntry,
} from '@/types/serviceReportTypes'
import type { ServiceEntryItem, ServiceReportFormData } from '@/types/serviceReportTypes'
import { generateOrderNumber, getNextMethodReportSequence } from '@/utils/getNextReportSequence'
import { getNdtExitPath } from '@/utils/ndtNavigation'
import { completeJobAssignmentsForEquipment, SERVICE_REPORT_WORK_TYPES } from '@/services/jobs.service'
import { useNdtPersonnel } from '@/hooks/useNdtPersonnel'
import { formatDateDDMMYYYY } from '@/services/ndtPdfShared'
import NdtFormPage, {
  NdtFormActions,
  ndtInputCls,
  ndtLabelCls,
  ndtSelectCls,
  ndtTextareaCls,
  ndtToggleCls,
  ndtUploadCls,
  ndtPrimaryBtnCls,
  ndtSecondaryBtnCls,
} from '@/components/ndt/NdtFormPage'
import NdtEquipmentSearch, { type NdtEquipmentHit } from '@/components/ndt/NdtEquipmentSearch'
import type { ServiceReportData } from '@/services/ProtoServiceTemplate'

const filledEntries = (items: ServiceEntryItem[]) =>
  items.filter((item) => item.value.trim() || item.details.trim())

const ServiceEntryList = ({
  title,
  items,
  onChange,
  options,
  valuePlaceholder,
  detailsPlaceholder,
}: {
  title: string
  items: ServiceEntryItem[]
  onChange: (items: ServiceEntryItem[]) => void
  options: readonly string[]
  valuePlaceholder: string
  detailsPlaceholder: string
}) => (
  <div>
    <label className={ndtLabelCls}>{title}</label>
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto] gap-2">
          <select
            value={item.value}
            onChange={(e) => {
              const next = [...items]
              next[index] = { ...item, value: e.target.value }
              onChange(next)
            }}
            className={ndtSelectCls}
          >
            <option value="">{valuePlaceholder}</option>
            {options.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <input
            type="text"
            value={item.details}
            onChange={(e) => {
              const next = [...items]
              next[index] = { ...item, details: e.target.value }
              onChange(next)
            }}
            className={ndtInputCls}
            placeholder={detailsPlaceholder}
          />
          {items.length > 1 ? (
            <button
              type="button"
              onClick={() => onChange(items.filter((_, i) => i !== index))}
              className="p-2 text-red-600 hover:bg-red-50 rounded-md min-h-[44px]"
            >
              <X size={16} />
            </button>
          ) : (
            <div />
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, emptyServiceEntry()])}
        className="inline-flex items-center gap-2 text-sm text-[#0D9488] font-medium min-h-[44px]"
      >
        <Plus size={18} /> Legg til rad
      </button>
    </div>
  </div>
)

const ServiceReportForm = () => {
  const navigate = useNavigate()
  const { inspectors, admins, companies, currentUser, loading, isInspectorLocked } = useNdtPersonnel()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null)

  const [formData, setFormData] = useState<ServiceReportFormData>({
    testDate: new Date().toISOString().split('T')[0],
    testSite: SERVICE_DEFAULT_SITE,
    duration: '',
    reportNumber: '',
    serialNumber: '',
    objectDescription: '',
    issuedBy: '',
    company: '',
    report_code: null,
    workPerformedItems: [emptyServiceEntry()],
    sparePartsItems: [emptyServiceEntry()],
    findings: '',
    workCompletion: '',
    recommendedActions: '',
    inlineImages: [],
  })

  useEffect(() => {
    if (isInspectorLocked && currentUser?.full_name && !formData.issuedBy) {
      setFormData((prev) => ({ ...prev, issuedBy: currentUser.full_name }))
    }
  }, [isInspectorLocked, currentUser, formData.issuedBy])

  const signerOptions = useMemo(() => {
    const byName = new Map<string, string>()
    for (const person of [...inspectors, ...admins]) {
      if (person.full_name) byName.set(person.full_name, person.full_name)
    }
    if (currentUser?.full_name) byName.set(currentUser.full_name, currentUser.full_name)
    return [...byName.keys()].sort((a, b) => a.localeCompare(b, 'nb'))
  }, [inspectors, admins, currentUser])

  const canSubmit = useMemo(() => {
    if (!formData.reportNumber.trim() || !formData.issuedBy.trim()) return false
    if (!formData.serialNumber.trim()) return false
    return true
  }, [formData])

  const selectedCompanyName = useMemo(
    () => companies.find((c) => c.id === formData.company)?.name ?? '',
    [companies, formData.company],
  )

  const handleSelectEquipment = (equipment: NdtEquipmentHit | null) => {
    setSelectedEquipmentId(equipment?.id ?? null)
    if (!equipment) return
    setFormData((prev) => ({
      ...prev,
      objectDescription: equipment.name || prev.objectDescription,
      serialNumber: equipment.serial_number || prev.serialNumber,
    }))
  }

  const handleCompanyChange = (companyId: string) => {
    const selected = companies.find((c) => c.id === companyId)
    setSelectedEquipmentId(null)
    if (!selected) {
      setFormData((prev) => ({ ...prev, company: '', report_code: null }))
      return
    }
    setFormData((prev) => ({
      ...prev,
      company: companyId,
      report_code: selected.report_code,
      reportNumber: selected.report_code ? generateOrderNumber(selected.report_code) : prev.reportNumber,
    }))
  }

  const buildPayload = useCallback(
    (reportId: string): ServiceReportData => {
      const dateLabel = formatDateDDMMYYYY(formData.testDate)
      const duration = formData.duration.trim()
      return {
        reportNumber: reportId,
        customer: selectedCompanyName,
        location: formData.testSite,
        dateDuration: duration ? `${dateLabel} / ${duration}` : dateLabel,
        object: formData.objectDescription.trim(),
        serialNumber: formData.serialNumber.trim(),
        workPerformedItems: filledEntries(formData.workPerformedItems),
        sparePartsItems: filledEntries(formData.sparePartsItems),
        findings: formData.findings || undefined,
        workCompletion: formData.workCompletion || undefined,
        recommendedActions: formData.recommendedActions.trim() || undefined,
        inlineImages: formData.inlineImages,
        status: formData.workCompletion === 'No' ? 'Not Accepted' : 'Accepted',
        issuedBy: formData.issuedBy.trim(),
        issuedDate: formData.testDate,
      }
    },
    [formData, selectedCompanyName],
  )

  const handlePreview = async () => {
    setIsGeneratingPreview(true)
    try {
      const { reportId } = await getNextMethodReportSequence('SR', formData.reportNumber)
      const blob = await generateServiceReport(buildPayload(reportId), { finalize: false })
      setPreviewUrl(URL.createObjectURL(blob))
    } catch (error) {
      console.error(error)
      alert('Kunne ikke generere forhåndsvisning.')
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!canSubmit) {
      alert('Vennligst fyll ut alle påkrevde felt.')
      return
    }
    setIsSubmitting(true)
    try {
      const { storageFolder, sequenceNumber, reportId } = await getNextMethodReportSequence('SR', formData.reportNumber)
      const fileName = `${reportId}.pdf`
      const folderPath = `${storageFolder}/${fileName}`
      const pdfBlob = await generateServiceReport(buildPayload(reportId), { finalize: true })

      const { error: uploadError } = await supabase.storage
        .from('reports')
        .upload(folderPath, pdfBlob, { contentType: 'application/pdf', upsert: true })
      if (uploadError) throw uploadError

      const { data: { user } } = await supabase.auth.getUser()
      const { inlineImages: _inline, ...stored } = formData
      const insertData: Record<string, unknown> = {
        order_number: reportId,
        unit_id: formData.serialNumber.trim() || formData.objectDescription.trim(),
        test_date: formData.testDate,
        test_type: 'SR',
        status: formData.workCompletion === 'No' ? 'Not Accepted' : 'Accepted',
        issued_by: formData.issuedBy,
        file_path: folderPath,
        report_code: formData.report_code,
        created_by: user?.id,
        report_data: {
          ...stored,
          sequenceNumber,
          equipmentId: selectedEquipmentId,
        },
      }
      if (formData.company?.trim()) insertData.company_id = formData.company
      if (selectedEquipmentId) insertData.equipment_id = selectedEquipmentId

      const { error: metadataError } = await supabase.from('ndt_reports').insert(insertData)
      if (metadataError) throw metadataError

      if (selectedEquipmentId && isSTMIDBCompany(formData.company)) {
        const equipment = await STMIDBEquipmentService.getEquipmentById(selectedEquipmentId)
        await STMIDBEquipmentService.uploadDocument({
          equipmentId: selectedEquipmentId,
          equipmentCode: getSTMIDBUploadCode({
            id: selectedEquipmentId,
            owner_id: equipment?.equipment_code,
            asset_number: equipment?.asset_number,
            serial_number: equipment?.serial_number,
          }),
          documentType: mapWLLDocumentTypeToSTMIDB('service_report'),
          file: new File([pdfBlob], fileName, { type: 'application/pdf' }),
        })
      } else if (selectedEquipmentId) {
        const equipmentDocFile = new File([pdfBlob], fileName, { type: 'application/pdf' })
        const equipmentUpload = await uploadDocument({
          file: equipmentDocFile,
          customerName: selectedCompanyName || 'unknown',
          serialNumber: formData.serialNumber.trim() || formData.objectDescription.trim() || reportId,
          equipmentId: selectedEquipmentId,
          documentType: 'service_report',
        })
        if (!equipmentUpload.success) {
          throw new Error(equipmentUpload.error || 'Kunne ikke lagre servicerapport på utstyr')
        }
      }

      const { data: signed } = await supabase.storage.from('reports').createSignedUrl(folderPath, 3600)
      if (signed?.signedUrl) window.open(signed.signedUrl, '_blank')

      alert(selectedEquipmentId
        ? 'Rapport generert og lagret på utstyr.'
        : 'Rapport generert og lagret.')
      if (selectedEquipmentId) void completeJobAssignmentsForEquipment(selectedEquipmentId, SERVICE_REPORT_WORK_TYPES)
      navigate(getNdtExitPath())
    } catch (error) {
      console.error(error)
      alert(error instanceof Error ? error.message : 'Feil ved generering av rapport. Prøv igjen.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInlineUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (!files.length) return
    Promise.all(
      files.map(
        (file) =>
          new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.readAsDataURL(file)
          }),
      ),
    ).then((urls) => {
      setFormData((prev) => ({
        ...prev,
        inlineImages: [...prev.inlineImages, ...urls].slice(0, 3),
      }))
    })
  }

  return (
    <NdtFormPage
      highlight="Service"
      title="Report"
      loading={loading}
      previewUrl={previewUrl}
      previewTitle="Servicerapport forhåndsvisning"
      onClosePreview={() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }}
      onPublish={() => void handleSubmit()}
      isPublishing={isSubmitting}
      canPublish={canSubmit}
      maxWidthClass="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Dato</label>
            <input type="date" required value={formData.testDate} onChange={(e) => setFormData((p) => ({ ...p, testDate: e.target.value }))} className={ndtInputCls} />
          </div>
          <div>
            <label className={ndtLabelCls}>Varighet (valgfritt)</label>
            <input type="text" value={formData.duration} onChange={(e) => setFormData((p) => ({ ...p, duration: e.target.value }))} className={ndtInputCls} placeholder="f.eks. 2 timer" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Sted</label>
            <select value={formData.testSite} onChange={(e) => setFormData((p) => ({ ...p, testSite: e.target.value }))} className={ndtSelectCls}>
              {SERVICE_REPORT_SITES.map((site) => (
                <option key={site} value={site}>{site}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={ndtLabelCls}>Kunde</label>
            <select value={formData.company || ''} onChange={(e) => handleCompanyChange(e.target.value)} className={ndtSelectCls}>
              <option value="">Velg kunde</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Vårt ordrenummer</label>
          <input type="text" required value={formData.reportNumber} onChange={(e) => setFormData((p) => ({ ...p, reportNumber: e.target.value }))} className={ndtInputCls} />
        </div>

        <NdtEquipmentSearch
          key={formData.company || 'none'}
          companyId={formData.company || ''}
          onSelect={handleSelectEquipment}
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Objekt</label>
            <input type="text" value={formData.objectDescription} onChange={(e) => setFormData((p) => ({ ...p, objectDescription: e.target.value }))} className={ndtInputCls} placeholder="f.eks. Power pack" />
          </div>
          <div>
            <label className={ndtLabelCls}>Serial No. / ID</label>
            <input type="text" required value={formData.serialNumber} onChange={(e) => setFormData((p) => ({ ...p, serialNumber: e.target.value }))} className={ndtInputCls} placeholder="Serienummer / ID" />
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Utført av</label>
          <select
            required
            value={formData.issuedBy}
            onChange={(e) => setFormData((p) => ({ ...p, issuedBy: e.target.value }))}
            className={ndtSelectCls}
            disabled={isInspectorLocked}
          >
            <option value="">Velg person</option>
            {signerOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        <ServiceEntryList
          title="Arbeid utført"
          items={formData.workPerformedItems}
          onChange={(workPerformedItems) => setFormData((p) => ({ ...p, workPerformedItems }))}
          options={SERVICE_WORK_PERFORMED_OPTIONS}
          valuePlaceholder="Velg arbeid"
          detailsPlaceholder="Detaljer"
        />

        <ServiceEntryList
          title="Reservedeler"
          items={formData.sparePartsItems}
          onChange={(sparePartsItems) => setFormData((p) => ({ ...p, sparePartsItems }))}
          options={SERVICE_SPARE_PARTS_OPTIONS}
          valuePlaceholder="Velg reservedel"
          detailsPlaceholder="Detaljer"
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ndtLabelCls}>Funn</label>
            <div className="flex gap-2">
              {(['Yes', 'None'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFormData((p) => ({ ...p, findings: p.findings === value ? '' : value }))}
                  className={ndtToggleCls(formData.findings === value)}
                >
                  {value === 'Yes' ? 'Ja' : 'Ingen'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={ndtLabelCls}>Arbeid fullført</label>
            <div className="flex gap-2">
              {(['Yes', 'No'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFormData((p) => ({ ...p, workCompletion: p.workCompletion === value ? '' : value }))}
                  className={ndtToggleCls(formData.workCompletion === value)}
                >
                  {value === 'Yes' ? 'Ja' : 'Nei'}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className={ndtLabelCls}>Anbefalte tiltak (valgfritt)</label>
          <textarea rows={3} value={formData.recommendedActions} onChange={(e) => setFormData((p) => ({ ...p, recommendedActions: e.target.value }))} className={ndtTextareaCls} />
        </div>

        <div>
          <label className={ndtLabelCls}>Bilder (maks 3)</label>
          <div className="space-y-3">
            {formData.inlineImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {formData.inlineImages.map((image, index) => (
                  <div key={`${image.slice(0, 24)}-${index}`} className="relative">
                    <img src={image} alt={`Bilde ${index + 1}`} className="w-full h-28 object-cover rounded-md border border-slate-200" />
                    <button
                      type="button"
                      onClick={() => setFormData((p) => ({
                        ...p,
                        inlineImages: p.inlineImages.filter((_, i) => i !== index),
                      }))}
                      className="absolute top-1 right-1 p-1.5 bg-red-600 text-white rounded-full"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {formData.inlineImages.length < 3 && (
              <label className={ndtUploadCls}>
                <Upload size={16} className="text-slate-400" />
                <span className="text-sm text-slate-600">Last opp bilde</span>
                <input type="file" accept="image/*" multiple onChange={handleInlineUpload} className="hidden" />
              </label>
            )}
          </div>
        </div>

        <NdtFormActions>
          <button type="button" onClick={() => void handlePreview()} disabled={isSubmitting || isGeneratingPreview} className={ndtSecondaryBtnCls}>
            {isGeneratingPreview ? 'Genererer…' : 'Forhåndsvis'}
          </button>
          <button type="submit" disabled={isSubmitting || !canSubmit} className={ndtPrimaryBtnCls}>
            {isSubmitting ? 'Genererer rapport...' : 'Generer servicerapport'}
          </button>
        </NdtFormActions>
      </form>
    </NdtFormPage>
  )
}

export default ServiceReportForm
